/**
 * enginePreProcess_Merge_TemplateTraversal.ts - Template traversal methods for EnginePreProcess
 * Port of C# EnginePreProcess_Merge_TemplateTraversal.cs
 * Contains processTemplateContent and resolveLatePlaceholder
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { PreprocessedTemplate, TemplateOperation, TemplateOperationKind, JsonArrayPlaceholder } from '../../model/index.js';
import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import type { EnginePreProcess } from './enginePreProcess.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import { CommonUtil } from '../../common/commonUtil.js';

/**
 * Walks a preprocessed template and resolves its placeholders and slots into final content.
 *
 * `latePlaceholders` carries the placeholder names that the DynamicView handler that produced
 * `content` introduced. They are resolved at the tail of this method, after the pre-parsed
 * slots/placeholders are merged — replacing the historical engine-side text scan with a
 * metadata-driven list supplied by the loader.
 */
export function processTemplateContent(this: EnginePreProcess, content: string, template: PreprocessedTemplate, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, latePlaceholders: string[] | null = null, clientMode: boolean = false): string {
    if (recursionDepth > this._loader!.maxRecursionDepth) {
        const errorMsg = `[ERROR] Maximum recursion depth (${this._loader!.maxRecursionDepth}) exceeded for template ${templateKey}. Possible circular template reference.`;
        Logger.error(errorMsg, 'EnginePreProcess');
        return errorMsg;
    }

    if (!content) {
        return content;
    }

    Logger.flow(`ProcessTemplateContent: enter templateKey=${templateKey} depth=${recursionDepth} slots=${template.slottedTemplates.length} placeholders=${template.placeholders.length} late=${latePlaceholders?.length ?? 0}`, "PreProcess.TemplateRecursion");
    let result = content;

    for (const slottedTemplate of template.slottedTemplates || []) {
        const slotIndex = result.indexOf(slottedTemplate.fullMatch);
        if (slotIndex < 0) {
            continue;
        }

        Logger.flow(`ProcessTemplateContent: slot=${slottedTemplate.name}`, "PreProcess.TemplateRecursion");
        const processedSlot = this._processSlottedTemplate(
            slottedTemplate,
            appSite,
            currentTemplateName,
            appView,
            templateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = CommonUtil.replaceAllFrom(result, slotIndex, slottedTemplate.fullMatch, processedSlot);
    }

    for (const placeholder of template.placeholders || []) {
        const placeholderIndex = result.indexOf(placeholder.fullMatch);
        if (placeholderIndex < 0) {
            continue;
        }

        Logger.flow(`ProcessTemplateContent: placeholder=${placeholder.name}`, "PreProcess.TemplateRecursion");
        const processedPlaceholder = this._processPlaceholder(
            placeholder,
            appSite,
            currentTemplateName,
            appView,
            templateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = CommonUtil.replaceAllFrom(result, placeholderIndex, placeholder.fullMatch, processedPlaceholder);
    }

    // Resolve any late-bound placeholders surfaced by the DynamicView handler that produced
    // `content`. The loader returns these names in order of first appearance — we honour that
    // order and advance a searchPos past each replacement to mirror the prior text-scan semantics.
    if (latePlaceholders && latePlaceholders.length > 0) {
        let searchPos = 0;
        for (const placeholderName of latePlaceholders) {
            const r = this._resolveLatePlaceholder(
                result,
                searchPos,
                placeholderName,
                appSite,
                currentTemplateName,
                appView,
                templateKey,
                operations,
                recursionDepth,
                componentHtml,
                genComponentHtml,
                contextJson,
                flowStack,
                configJson,
                clientMode
            );
            result = r.result;
            searchPos = r.searchPos;
        }
    }

    return result;
}

/**
 * Resolves a single late-bound placeholder (one entry from the loader's post-handler scan)
 * by loading its referenced template, applying its handlers, merging JSON, and recursing.
 * Replaces the next occurrence of {{placeholderName}} in `content` starting from `searchPos`,
 * then returns the updated content and an advanced searchPos past the inserted replacement.
 */
export function resolveLatePlaceholder(this: EnginePreProcess, content: string, searchPos: number, placeholderName: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): { result: string; searchPos: number } {
    const fullMatch = '{{' + placeholderName + '}}';
    const openStart = content.indexOf(fullMatch, searchPos);
    if (openStart < 0) {
        // Already replaced by an earlier iteration (duplicate name) or no longer present.
        return { result: content, searchPos };
    }
    const closeStart = openStart + fullMatch.length - 2;

    const nestedOperations: TemplateOperation[] = [];
    const newFlowStack = [...(flowStack || []), placeholderName];

    let { template: childTemplate, json: childJson, resolvedTemplateName, resolvedAppSite, viewHtml: wrappedHtml, latePlaceholders: replacementLatePlaceholders } =
        this._loader!.getTemplateWithView(appSite, currentTemplateName, placeholderName, appView, newFlowStack, configJson, clientMode);
    if (wrappedHtml !== childTemplate.originalContent) {
        const wrappedTemplate = new PreprocessedTemplate();
        wrappedTemplate.originalContent = wrappedHtml;
        wrappedTemplate.actualTemplateName = childTemplate.actualTemplateName;
        wrappedTemplate.placeholders = childTemplate.placeholders;
        wrappedTemplate.slottedTemplates = childTemplate.slottedTemplates;
        wrappedTemplate.replacementMappings = childTemplate.replacementMappings;
        wrappedTemplate.jsonArrayPlaceholders = childTemplate.jsonArrayPlaceholders;
        childTemplate = wrappedTemplate;
    }

    if (resolvedTemplateName && resolvedTemplateName !== placeholderName && newFlowStack.length > 0) {
        newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
    }

    let replacement: string;
    if (childTemplate == null) {
        replacement = this._loader!.defaultReplaceHtml;
        resolvedTemplateName = this._loader!.defaultReplaceTemplateName;
        resolvedAppSite = appSite;
    } else {
        replacement = childTemplate.originalContent || '';
    }

    const replacedWith = `${resolvedAppSite}_${resolvedTemplateName || placeholderName}`;
    const isPlaceholderSkipMarked = placeholderName.startsWith('!');
    const isSkippedOrMissing = resolvedTemplateName === this._loader!.defaultSkipTemplateName || resolvedTemplateName === this._loader!.defaultReplaceTemplateName;

    if (childTemplate && childTemplate.originalContent && replacement && !isPlaceholderSkipMarked && !isSkippedOrMissing) {
        const jsonToMerge = childJson || new JsonObject();
        const arrayMetadata: JsonArrayPlaceholder[] | null = childTemplate.jsonArrayPlaceholders && childTemplate.jsonArrayPlaceholders.length > 0
            ? childTemplate.jsonArrayPlaceholders
            : null;
        const jsonKeyMetadata = childTemplate.jsonKeyPlaceholders && childTemplate.jsonKeyPlaceholders.length > 0
            ? childTemplate.jsonKeyPlaceholders
            : null;
        replacement = JsonMergeUtil.mergeTemplateWithJson(
            replacement,
            jsonToMerge,
            replacedWith,
            resolvedTemplateName || placeholderName,
            nestedOperations,
            this._loader!.maxArrayOperationItems,
            this._loader!.defaultReplaceHtml,
            this._loader!.defaultSkipJson,
            arrayMetadata,
            jsonKeyMetadata,
            contextJson,
            configJson
        );
        this._jsonMergeCount++;

        if (genComponentHtml) {
            componentHtml.set(placeholderName, JsonValue.string(''));
        }

        replacement = this._processTemplateContent(
            replacement,
            childTemplate,
            appSite,
            placeholderName,
            appView,
            replacedWith,
            nestedOperations,
            recursionDepth + 1,
            componentHtml,
            genComponentHtml,
            contextJson,
            newFlowStack,
            configJson,
            replacementLatePlaceholders,
            clientMode
        );

        if (genComponentHtml) {
            componentHtml.set(placeholderName, JsonValue.string(replacement));
        }
    }

    let opType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
    if (resolvedTemplateName === this._loader!.defaultSkipTemplateName) {
        opType = TemplateOperationKind.ComponentSkip;
    } else if (resolvedTemplateName === this._loader!.defaultReplaceTemplateName) {
        opType = TemplateOperationKind.ComponentMissing;
    }

    operations.push(new TemplateOperation({
        templateName: templateKey,
        kind: opType,
        target: fullMatch,
        replacedWith: replacedWith,
        operations: nestedOperations
    }));

    const result = content.substring(0, openStart) + replacement + content.substring(closeStart + 2);
    return { result, searchPos: openStart + replacement.length };
}
